import React, { Dispatch, SetStateAction, useContext } from 'react'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import dateService from 'live/services/date'
import { getAssetLink, getColorFromResult } from '../../common/utils'
import { analysisEditingUrl } from '../../qualityAnalysis/analysisUtils'
import { ParamsContext } from '../qualityMonitoring'

interface IProps {
    filteredAnalysis: AnalysisEvent[]
    setSelectedAnalysis: Dispatch<SetStateAction<AnalysisEvent>>
    setSelectedStoppedLine: Dispatch<SetStateAction<StoppedLineEvent>>
}
export function AnalysisListTable({
    filteredAnalysis,
    setSelectedAnalysis,
    setSelectedStoppedLine
}: IProps): JSX.Element {
    const urlParams = useContext(ParamsContext)
    const paramsArray = urlParams.split('/')

    function getActionPlanIcon(status: boolean): JSX.Element {
        switch (status) {
            case false:
                return <i className="fas fa-hourglass-half" />
            case true:
                return <i className="fas fa-check-circle" />
            default:
                return <span>--</span>
        }
    }

    function handleRowClick(
        type: AnalysisTypes | 'stopped line',
        analysisResult: AnalysisEvent | StoppedLineEvent
    ): void {
        if (type.toLowerCase() === 'stopped line') {
            setSelectedStoppedLine(analysisResult as StoppedLineEvent)
        } else {
            const { country, factory, line, product } = analysisResult as AnalysisEvent
            window.location.href = getAssetLink(
                `${paramsArray[0]}/${country}/${factory}/${line}/${product ? product + '/' : ''}${analysisEditingUrl}`
            )
            setSelectedAnalysis(analysisResult as AnalysisEvent)
        }
    }

    return (
        <StyledAnalysisListTable className="analysisListTable">
            <table className="analysisListTable--headerContainer">
                <thead className="analysisListTable--header">
                    <tr>
                        <th>SKU</th>
                        <th>{i18n('Batch')}</th>
                        <th>{i18n('Result')}</th>
                        <th>{i18n('Type')}</th>
                        <th>{i18n('Author')}</th>
                        <th>{i18n('Date')}</th>
                        <th>{i18n('Action Plan')}</th>
                        {paramsArray.length < 4 ? <th>{i18n('line')}</th> : null}
                        {paramsArray.length < 3 ? <th>{i18n('factory')}</th> : null}
                        {paramsArray.length < 2 ? <th>{i18n('country')}</th> : null}
                    </tr>
                </thead>
            </table>
            <div className="analysisListTable--scroll">
                <table>
                    <tbody>
                        {filteredAnalysis.map((analysisResult, index) => {
                            return (
                                <tr
                                    key={index}
                                    className="analysisListTable--row"
                                    onClick={() => handleRowClick(analysisResult.analysisType, analysisResult)}
                                >
                                    <td className="analysisListTable--row__sku">{analysisResult.sku || '--'}</td>
                                    <td className="analysisListTable--row__batch">{analysisResult.batch || '--'}</td>
                                    <td className={'analysisListTable--row__result'}>
                                        {'finalScore' in analysisResult ? (
                                            <div
                                                className={'analysisListTable--row__result--resultPill'}
                                                style={
                                                    {
                                                        '--gradeColor': getColorFromResult(analysisResult.finalScore)
                                                    } as React.CSSProperties
                                                }
                                            >
                                                {analysisResult.finalScore}%
                                            </div>
                                        ) : (
                                            '--'
                                        )}
                                    </td>
                                    <td>{i18n(analysisResult.analysisType).toUpperCase()}</td>
                                    <td>{analysisResult.analysisCreator}</td>
                                    <td>{dateService.formatDateTime(analysisResult.createdAt)}</td>
                                    <td>{getActionPlanIcon(analysisResult.actionPlans?.[0]?.planStatus)}</td>
                                    {paramsArray.length < 4 ? <td>{analysisResult.line}</td> : null}
                                    {paramsArray.length < 3 ? <td>{analysisResult.factory}</td> : null}
                                    {paramsArray.length < 2 ? <td>{analysisResult.country}</td> : null}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </StyledAnalysisListTable>
    )
}

const StyledAnalysisListTable = styled.div`
    width: 100%;
    height: 100%;
    tbody {
        height: 100%;
    }
    .analysisListTable {
        &--scroll {
            overflow-y: auto;
            /* overflow-x: hidden; */
            height: 380px;
        }
        &--headerContainer {
            height: 33px;
        }
        &--header {
            background: ${({ theme }) => theme['gray-200']};
            font-family: ${({ theme }) => theme['font-semibold']};
            color: ${({ theme }) => theme['gray-700']};
            th {
                text-transform: capitalize;
                padding: 10px 20px;
            }
            height: 33px;
        }
        &--row {
            &:hover {
                td {
                    background: ${({ theme }) => theme['blue-200']};
                }
            }
            td {
                padding: 20px;
                color: ${({ theme }) => theme['gray-800']};
                font-size: 14px;
                transition: all 200ms ease-in-out;
                /* & > * {
                    vertical-align: middle;
                } */
            }
            &__result {
                &--resultPill {
                    padding: 5px 10px;
                    color: ${({ theme }) => theme['gray-0']};
                    border-radius: 20px;
                    background: var(--gradeColor);
                    white-space: nowrap;
                    width: 60px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
            &__sku,
            &__batch {
                font-family: ${({ theme }) => theme['font-semibold']};
            }
            .fa-hourglass-half {
                color: ${({ theme }) => theme['red-700']};
            }
            .fa-check-circle {
                color: ${({ theme }) => theme['green-700']};
            }
        }
    }

    table {
        width: 100%;
        border-spacing: 0;
        height: 100%;
    }

    tbody {
        display: block;
    }

    tr {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    }

    tbody {
        overflow-x: hidden;
    }
`
